import React, { useState, useEffect } from 'react';
import { clientsApi } from './services/api';
import { ClientData, UpdateResult, PaymentTypeFilter, ModuleFilter, MembershipStatusFilter } from './types';
import DateFilter from './components/DateFilter';
import ViewToggle, { ViewType } from './components/ViewToggle';
import StatusFilter, { ClientStatusFilter } from './components/StatusFilter';
import PaymentTypeFilterComponent from './components/PaymentTypeFilter';
import ModuleFilterComponent from './components/ModuleFilter';
import MembershipStatusFilterComponent from './components/MembershipStatusFilter';
import UpdateStatus from './components/UpdateStatus';
import ActionBar from './components/ActionBar';
import ClientsTable from './components/ClientsTable';
import UsageChart from './components/UsageChart';
import DailyUsageTable from './components/DailyUsageTable';
import DailyConsumptionChart from './components/DailyConsumptionChart';
import ErrorReport from './components/ErrorReport';
import { Activity, AlertTriangle, Calendar, Users, TrendingUp } from 'lucide-react';
import './App.css';

type TabType = 'clients' | 'daily' | 'chart';

function App() {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estado para filtros de fecha
  const currentDate = new Date();
  const [year, setYear] = useState(currentDate.getFullYear());
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  
  // Estado para pestañas
  const [activeTab, setActiveTab] = useState<TabType>('clients');
  
  // Estado para tipo de vista (mensual vs ciclo)
  const [viewType, setViewType] = useState<ViewType>('monthly');
  
  // Estado para filtro de estado
  const [selectedStatus, setSelectedStatus] = useState<ClientStatusFilter>('all');
  
  // Estado para filtro de tipo de pago
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentTypeFilter>('all');
  
  // Estado para filtro de módulos
  const [selectedModule, setSelectedModule] = useState<ModuleFilter>('all');
  
  // Estado para filtro de membresía
  const [selectedMembershipStatus, setSelectedMembershipStatus] = useState<MembershipStatusFilter>('all');
  
  // Estado para el reporte de errores
  const [lastUpdateResult, setLastUpdateResult] = useState<UpdateResult | null>(null);
  const [showErrorReport, setShowErrorReport] = useState(false);
  
  // Estado para datos de uso diario
  const [dailyUsageData, setDailyUsageData] = useState<any>(null);
  const [dailyUsageLoading, setDailyUsageLoading] = useState(false);

  // Función para cargar datos de clientes
  const loadClientsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Usar el endpoint apropiado según el tipo de vista
      const response = viewType === 'cycle' 
        ? await clientsApi.getClientsByCycle(year, month)
        : await clientsApi.getClients(year, month);
      
      if (response.success) {
        setClients(response.data);
      } else {
        throw new Error(response.error || 'Error al cargar datos');
      }
    } catch (err) {
      console.error('Error loading clients:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar datos de uso diario
  const loadDailyUsageData = async () => {
    try {
      setDailyUsageLoading(true);
      setError(null);
      
      const response = await clientsApi.getDailyUsage(year, month);
      
      if (response.success) {
        setDailyUsageData(response.data);
      } else {
        throw new Error(response.error || 'Error al cargar datos de uso diario');
      }
    } catch (err) {
      console.error('Error loading daily usage:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setDailyUsageLoading(false);
    }
  };

  // Función para actualizar un cliente específico sin recargar todos los datos
  const handleClientUpdate = (clientName: string, updatedData: Partial<ClientData>) => {
    setClients(prevClients => 
      prevClients.map(client => 
        client.name === clientName 
          ? { ...client, ...updatedData }
          : client
      )
    );
  };

  // Cargar datos cuando cambian los filtros o la pestaña activa
  useEffect(() => {
    if (activeTab === 'clients') {
      loadClientsData();
    } else if (activeTab === 'daily') {
      loadDailyUsageData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, viewType, activeTab]);

  // Health check inicial
  useEffect(() => {
    const checkHealth = async () => {
      try {
        await clientsApi.healthCheck();
        console.log('✅ API connection successful');
      } catch (error) {
        console.error('❌ API connection failed:', error);
        setError('No se puede conectar con el servidor. Verifica que esté ejecutándose.');
      }
    };
    
    checkHealth();
  }, []);

  const handleDataUpdated = () => {
    if (activeTab === 'clients') {
      loadClientsData();
    } else if (activeTab === 'daily') {
      loadDailyUsageData();
    }
  };

  const handleUpdateResult = (result: UpdateResult | null) => {
    setLastUpdateResult(result);
    // Mostrar automáticamente el reporte de errores si hay errores
    if (result && result.errorCount > 0) {
      setShowErrorReport(true);
    }
  };

  const handleYearChange = (newYear: number) => {
    setYear(newYear);
  };

  const handleMonthChange = (newMonth: number) => {
    setMonth(newMonth);
  };

  const handleStatusChange = (status: ClientStatusFilter) => {
    setSelectedStatus(status);
  };

  const handlePaymentTypeChange = (type: PaymentTypeFilter) => {
    setSelectedPaymentType(type);
  };

  const handleViewChange = (newViewType: ViewType) => {
    setViewType(newViewType);
  };

  // Filtrar clientes según el estado y tipo de pago seleccionados
  const filteredClients = clients.filter(client => {
    // Filtro por estado
    let statusMatch = true;
    if (selectedStatus !== 'all') {
      if (selectedStatus === 'demo') statusMatch = client.clientStatus === 'demo';
      else if (selectedStatus === 'paid') statusMatch = client.clientStatus === 'paid';
      else if (selectedStatus === 'inactive') statusMatch = client.clientStatus === 'inactive';
      else if (selectedStatus === 'terminated') statusMatch = client.clientStatus === 'terminated';
      else if (selectedStatus === 'demo_terminated') statusMatch = client.clientStatus === 'demo_terminated';
    }
    
    // Filtro por tipo de pago
    let paymentTypeMatch = true;
    if (selectedPaymentType !== 'all') {
      if (selectedPaymentType === 'recurrent') paymentTypeMatch = !client.isUniquePayment;
      else if (selectedPaymentType === 'package') paymentTypeMatch = Boolean(client.isUniquePayment);
    }
    
    // Filtro por módulos
    let moduleMatch = true;
    if (selectedModule !== 'all') {
      const clientModules = client.modulesEnabled || [];
      moduleMatch = clientModules.includes(selectedModule);
    }
    
    // Filtro por estado de membresía (solo aplicable a clientes pagos)
    let membershipMatch = true;
    if (selectedMembershipStatus !== 'all') {
      // Si hay un filtro de membresía activo, SOLO mostrar clientes pagos
      if (client.clientStatus !== 'paid') {
        membershipMatch = false;
      } else {
        // Es un cliente pago, verificar el membership status
        if (selectedMembershipStatus === 'active') {
          membershipMatch = client.membershipStatus === 1;
        } else if (selectedMembershipStatus === 'deactivated') {
          membershipMatch = client.membershipStatus === 0;
        }
      }
    }
    
    return statusMatch && paymentTypeMatch && moduleMatch && membershipMatch;
  });

  // Calcular estadísticas para el filtro de estado
  const demoCount = clients.filter(c => c.clientStatus === 'demo').length;
  const paidCount = clients.filter(c => c.clientStatus === 'paid').length;
  const inactiveCount = clients.filter(c => c.clientStatus === 'inactive').length;
  const terminatedCount = clients.filter(c => c.clientStatus === 'terminated').length;
  const demoTerminatedCount = clients.filter(c => c.clientStatus === 'demo_terminated').length;
  const totalCount = clients.length;

  // Calcular estadísticas para el filtro de tipo de pago
  const recurrentCount = clients.filter(c => !c.isUniquePayment).length;
  const packageCount = clients.filter(c => Boolean(c.isUniquePayment)).length;

  // Calcular estadísticas para el filtro de módulos
  const causacionCount = clients.filter(c => (c.modulesEnabled || []).includes('use_accounting_system')).length;
  const acuseCount = clients.filter(c => (c.modulesEnabled || []).includes('acuse_recibo_module')).length;

  // Calcular estadísticas para el filtro de membresía (solo clientes pagos)
  const paidClientsOnly = clients.filter(c => c.clientStatus === 'paid');
  const activeCount = paidClientsOnly.filter(c => c.membershipStatus === 1).length;
  const deactivatedCount = paidClientsOnly.filter(c => c.membershipStatus === 0).length;

  if (error) {
    return (
      <div className="app">
        <div className="error-container">
          <AlertTriangle size={48} color="#EF4444" />
          <h2>Error de Conexión</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <Activity size={32} />
            <h1>Dashboard de Clientes</h1>
          </div>
          <p className="header-subtitle">
            Monitoreo de uso diario por cliente
          </p>
        </div>
      </header>

      <main className="app-main">
        <div className="container">
          {/* Filtros de fecha */}
          <DateFilter
            year={year}
            month={month}
            onYearChange={handleYearChange}
            onMonthChange={handleMonthChange}
          />

          {/* Estado de actualización */}
          <UpdateStatus />

          {/* Pestañas de navegación */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
                onClick={() => setActiveTab('clients')}
              >
                <Users size={18} />
                Vista de Clientes
              </button>
              <button 
                className={`tab ${activeTab === 'daily' ? 'active' : ''}`}
                onClick={() => setActiveTab('daily')}
              >
                <Calendar size={18} />
                Uso Diario
              </button>
              <button 
                className={`tab ${activeTab === 'chart' ? 'active' : ''}`}
                onClick={() => setActiveTab('chart')}
              >
                <TrendingUp size={18} />
                Gráfica de Consumo
              </button>
            </div>
          </div>

          {/* Contenido según la pestaña activa */}
          {activeTab === 'clients' && (
            <>
              {/* Toggle de vista */}
              <ViewToggle
                viewType={viewType}
                onViewChange={handleViewChange}
              />

              {/* Filtro de estado */}
              <StatusFilter
                selectedStatus={selectedStatus}
                onStatusChange={handleStatusChange}
                demoCount={demoCount}
                paidCount={paidCount}
                inactiveCount={inactiveCount}
                terminatedCount={terminatedCount}
                demoTerminatedCount={demoTerminatedCount}
                totalCount={totalCount}
              />

              {/* Filtro de tipo de pago */}
              <PaymentTypeFilterComponent
                selectedType={selectedPaymentType}
                onTypeChange={handlePaymentTypeChange}
                recurrentCount={recurrentCount}
                packageCount={packageCount}
                totalCount={totalCount}
              />

              {/* Filtro por módulos */}
              <ModuleFilterComponent
                selectedModule={selectedModule}
                onModuleChange={setSelectedModule}
                causacionCount={causacionCount}
                acuseCount={acuseCount}
                totalCount={totalCount}
              />

              {/* Filtro de estado de membresía */}
              <MembershipStatusFilterComponent
                selectedMembershipStatus={selectedMembershipStatus}
                onMembershipStatusChange={setSelectedMembershipStatus}
                activeCount={activeCount}
                deactivatedCount={deactivatedCount}
                totalPaidCount={paidCount}
              />

              {/* Barra de acciones */}
              <ActionBar
                year={year}
                month={month}
                onDataUpdated={handleDataUpdated}
                onUpdateResult={handleUpdateResult}
              />

              {/* Gráficas de uso */}
              <UsageChart
                clients={filteredClients}
                year={year}
                month={month}
              />

              {/* Tabla de clientes */}
              <ClientsTable
                clients={filteredClients}
                loading={loading}
                year={year}
                month={month}
                onClientUpdate={handleClientUpdate}
              />
            </>
          )}

          {activeTab === 'daily' && (
            <>
              {/* Barra de acciones para uso diario */}
              <ActionBar
                year={year}
                month={month}
                onDataUpdated={handleDataUpdated}
                onUpdateResult={handleUpdateResult}
              />

              {/* Tabla de uso diario */}
              <DailyUsageTable
                data={dailyUsageData}
                isLoading={dailyUsageLoading}
              />
            </>
          )}

          {activeTab === 'chart' && (
            <>
              {/* Gráfica de consumo diario */}
              <DailyConsumptionChart />
            </>
          )}

          {/* Reporte de errores al final - solo mostrar si hay errores */}
          {lastUpdateResult && lastUpdateResult.errorCount > 0 && (
            <ErrorReport 
              updateResult={lastUpdateResult}
              isVisible={showErrorReport}
              onToggle={() => setShowErrorReport(!showErrorReport)}
            />
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="container">
          <p>&copy; 2024 Dashboard de Clientes. Desarrollado para monitoreo de uso.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;